import * as runtime from '../runtime';
import type {
  GetFavoritesResponse,
  GetFavoriteStatusResponse,
  ToggleFavoriteResponse,
} from '../models';

interface ToggleFavoriteRequest {
  listingId: number;
}

export interface GetFavoriteStatusRequest {
  listingId: number;
}

/**
 * API for managing a buyer's favourite listings.
 */
export class FavoriteApi extends runtime.BaseAPI {
  // ── Toggle (add / remove) ──────────────────────────────────────────────────

  async toggleFavoriteRaw(
    requestParameters: ToggleFavoriteRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<ToggleFavoriteResponse>> {
    if (requestParameters.listingId == null) {
      throw new runtime.RequiredError(
        'listingId',
        'Required parameter requestParameters.listingId was null or undefined when calling toggleFavorite.',
      );
    }

    const headerParameters: runtime.HTTPHeaders = {};
    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/favorites`,
        method: 'POST',
        headers: headerParameters,
        body: { listingId: requestParameters.listingId },
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  async toggleFavorite(
    requestParameters: ToggleFavoriteRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ToggleFavoriteResponse> {
    const response = await this.toggleFavoriteRaw(requestParameters, initOverrides);
    return response.value();
  }

  // ── Get all favourites ─────────────────────────────────────────────────────

  async getFavoritesRaw(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetFavoritesResponse>> {
    const response = await this.request(
      {
        path: `/favorites`,
        method: 'GET',
        headers: {},
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  async getFavorites(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetFavoritesResponse> {
    const response = await this.getFavoritesRaw(initOverrides);
    return response.value();
  }

  // ── Check favourite status for a single listing ────────────────────────────

  async getFavoriteStatusRaw(
    requestParameters: GetFavoriteStatusRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetFavoriteStatusResponse>> {
    if (requestParameters.listingId == null) {
      throw new runtime.RequiredError(
        'listingId',
        'Required parameter requestParameters.listingId was null or undefined when calling getFavoriteStatus.',
      );
    }

    const queryParameters: Record<string, string | number | boolean> = {
      listingId: requestParameters.listingId,
    };

    const response = await this.request(
      {
        path: `/favorites/status`,
        method: 'GET',
        headers: {},
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  async getFavoriteStatus(
    requestParameters: GetFavoriteStatusRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetFavoriteStatusResponse> {
    const response = await this.getFavoriteStatusRaw(requestParameters, initOverrides);
    return response.value();
  }

  // ── Legacy method kept for backward compatibility ──────────────────────────

  /** @deprecated Use toggleFavorite instead */
  async addFavoriteListing(
    requestParameters: { id: number },
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ToggleFavoriteResponse> {
    return this.toggleFavorite({ listingId: requestParameters.id }, initOverrides);
  }
}
